import { ORDER_STATUS } from "../../../Constants/AppConstants";
import { ENHANCED_RFQ_PROTOCOLS } from "./RfqConstants";

/**
 * Parse venues from the order data
 */
const findBestPrice = (quotes, isBuyOrder, protocol = null, rfqStatus = null, rfqInquiryType = null) => {
  // NEW: if RFQ is traded or expired, do not highlight any price
  const norm = (s) => (s ? String(s).trim().toUpperCase() : "");
  const sOrder = norm(rfqStatus);
  if (sOrder === "TRADED" || sOrder === "EXPIRED") return null;

  if (!quotes || quotes.length === 0) return null;

  // Use only quote.price, no effective/counter logic, no status exclusions
  const priced = quotes
    .map((q) => {
      const hasPrice = q?.price !== null && q?.price !== undefined && q?.price !== "";
      const numeric = hasPrice ? Number(q.price) : NaN;
      return {
        orderQuotesId: q.orderQuotesId,
        price: numeric,
      };
    })
    .filter((p) => Number.isFinite(p.price));

  if (priced.length === 0) return null;

  const target = isBuyOrder
    ? Math.min(...priced.map((p) => p.price))
    : Math.max(...priced.map((p) => p.price));

  const match = priced.find((p) => p.price === target);
  return match ? match.orderQuotesId : null;
};

export const parseVenues = (data) => {
  if (!data || !data.orderQuotes || !Array.isArray(data.orderQuotes))
    return [];

  const isBuyOrder = data.side?.toUpperCase() === 'BUY';
  const bestPriceQuoteId = findBestPrice(
    data.orderQuotes, 
    isBuyOrder, 
    data.protocol, 
    data.rfqStatus, 
    data.inquiryType || data.rfqInquiryType
  );

  // helper: format Date in UTC as YYYYMMDD-HH:MM:SS (used elsewhere if needed)
  const fmtUTC = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    const z = (n) => String(n).padStart(2, "0");
    const Y = d.getUTCFullYear();
    const M = z(d.getUTCMonth() + 1);
    const D = z(d.getUTCDate());
    const hh = z(d.getUTCHours());
    const mm = z(d.getUTCMinutes());
    const ss = z(d.getUTCSeconds());
    return `${Y}${M}${D}-${hh}:${mm}:${ss}`;
  };

  // NEW: helper to return time-only (HH:MM:SS) in UTC
  const fmtTimeOnly = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return null;
    const z = (n) => String(n).padStart(2, "0");
    const hh = z(d.getUTCHours());
    const mm = z(d.getUTCMinutes());
    const ss = z(d.getUTCSeconds());
    return `${hh}:${mm}:${ss}`;
  };

  return data.orderQuotes.map((quote) => {
    // NEW: derive quoteExpiryDtTime from several possible fields and format to time-only if possible
    const rawWire = quote.quoteExpiryDtTime || quote.wireTransferTime || quote.wireTransferDtTime || null;
    let quoteExpiryDtTimeFormatted = undefined;

    if (rawWire) {
      try {
        // Simple normalization:
        // If it's an ISO-like string with 'T' and no timezone marker (no trailing 'Z' and no +HH or -HH offset),
        // treat it as UTC by appending 'Z'.
        let normalized = rawWire;
        if (typeof normalized === "string") {
          const hasT = normalized.indexOf("T") !== -1;
          const hasZ = normalized.toUpperCase().endsWith("Z");
          const hasOffset = /[+-]\d{2}(:?\d{2})?$/.test(normalized);
          if (hasT && !hasZ && !hasOffset) {
            normalized = normalized + "Z";
          }
        }
        const d = new Date(normalized);
        const timeOnly = fmtTimeOnly(d);
        quoteExpiryDtTimeFormatted = timeOnly || undefined;
      } catch (e) {
        quoteExpiryDtTimeFormatted = undefined;
      }
    } else if (quote.ttl) {
      // keep ttl as fallback (string/number)
      quoteExpiryDtTimeFormatted = String(quote.ttl);
    }

    const hasNumericPrice = quote?.price !== null && quote?.price !== undefined && quote?.price !== "" && !isNaN(Number(quote.price));
    const numericPrice = hasNumericPrice ? Number(quote.price) : NaN;
    const formattedPrice = Number.isFinite(numericPrice) ? numericPrice.toFixed(2) : "---";

    return {
      orderQuotesId: quote.orderQuotesId,
      orderId: quote.orderId || data.orderId,
      venue: quote.venue || "---",
      qty: quote.qty || "---",
      price: formattedPrice,
      counterPrice: quote.counterPrice
        ? parseFloat(quote.counterPrice).toFixed(2)
        : "---",
      acceptedPrice: quote.acceptedPrice
        ? parseFloat(quote.acceptedPrice).toFixed(2)
        : "---",
      // quoteExpiryDtTime now returns time-only (HH:MM:SS) or ttl or "N/A"
      quoteExpiryDtTime:
        quoteExpiryDtTimeFormatted === undefined ||
        quoteExpiryDtTimeFormatted === null ||
        (typeof quoteExpiryDtTimeFormatted === "string" && quoteExpiryDtTimeFormatted.trim() === "")
          ? "N/A"
          : quoteExpiryDtTimeFormatted,
      status: quote.status || "---",
      displayQuoteStatus: quote.displayQuoteStatus || null,
      isActive: quote.isActive,
      isBestPrice: Number.isFinite(numericPrice) && quote.orderQuotesId === bestPriceQuoteId,
      side: data.side // Add order side to each venue
    };
  });
};

/**
 * Determine if a button should be enabled based on status
 * Enhanced: supports Trumid-style matrix when protocol matches ENHANCED_RFQ_PROTOCOLS,
 * and enforces order-level rfqStatus gates (TRADED/EXPIRED/MANUAL_TAKE_OVER).
 * Backward compatible: old call sites using (status, buttonType) continue to work.
 */
export const isButtonEnabled = (
  status, 
  buttonType, 
  currentVenue = null, 
  venues = null, 
  protocol = null,
  rfqStatus = null,          // NEW
  rfqInquiryType = null      // NEW
) => {
  if (!status) return false;

  // Helper utils
  const norm = (s) => (s ? String(s).trim().toUpperCase() : "");
  const isEnhancedProtocol = protocol
    ? ENHANCED_RFQ_PROTOCOLS.some(p => p.toLowerCase() === String(protocol).toLowerCase())
    : false;

  if (isEnhancedProtocol) {
    // Order-level rfqStatus gates
    const sOrder = norm(rfqStatus);
    const inquiry = norm(rfqInquiryType);

    // If RFQ traded/expired or manual takeover -> disable all buttons
    if (sOrder === "TRADED" || sOrder === "EXPIRED" || sOrder === "MANUAL_TAKE_OVER") {
      return false;
    }
    // If OPEN with BIN inquiry -> column is hidden; keep buttons disabled here too
    if (sOrder === "OPEN" && inquiry === "BIN") {
      return false;
    }

    // Check if ANY venue has ACCEPTED, ACCEPTANCE_ACKED or COUNTER_ACCEPTED status - disable all
    // buttons for all rows. COUNTER_ACCEPTED: the dealer took our counter, so the bond is trading.
    if (venues && Array.isArray(venues)) {
      const hasAcceptedOrAcked = venues.some(venue => {
        const venueStatus = norm(venue.status);
        return venueStatus === "ACCEPTED" || venueStatus === "ACCEPTANCE_ACKED"
          || venueStatus === "COUNTER_ACCEPTED";
      });
      if (hasAcceptedOrAcked) {
        return false;
      }
    }

    // Trumid RFQ quote-level rule matrix - PER-ROW ONLY
    const s = norm(status);

    // Special handling for cancel counter button
    if (buttonType === "counter_cancel") {
      // Enable cancel counter button only for these specific statuses
      return s === "COUNTER_SUBMITTED_ACKED" || 
             s === "COUNTER_CANCELED_FAILED"
    }

    // Special handling for COUNTER_CANCELED_ACKED - enable all buttons except counter_cancel
    if (s === "COUNTER_CANCELED_ACKED") {
      return buttonType !== "counter_cancel";
    }

    // Special handling for COUNTER_SUBMITTED_ACKED - enable only reject button
    if (s === "COUNTER_SUBMITTED_ACKED") {
      return buttonType === "reject";
    }

    // Per-row enabling conditions (enable THIS row only)
    if (
      s === "RECEIVED" ||
      s === "ACCEPTANCE_FAILED" ||
      s === "REJECTION_FAILED" ||
      s === "COUNTER_SUBMITTED_REJECTED" ||
      s === "COUNTER_RECEIVED" || s === "COUNTER_RECIEVED"
    ) {
      return true;
    }

    // Per-row disabling conditions (disable THIS row only)
    if (
      s === "ACCEPTED" ||
      s === "ACCEPTANCE_ACKED" ||
      s === "ACCEPTANCE_REJECTED" ||
      s === "REJECTED" ||
      s === "REJECTION_ACKED" ||
      s === "COUNTER_SUBMITTED" ||
      s === "COUNTER_REJECTED" ||
      s === "QUOTE_CANCELED" ||
      s === "PASSED" ||
      s === "COUNTER_CANCELED"
    ) {
      return false;
    }

    // Note: COUNTER_ACCEPTED is handled by action column visibility, not here
    // as per spec: "EnableButtons": "N/A", "ShowActionColumn": "No"

    // All other statuses default to disabled
    return false;
  }

  // Legacy/default behavior
  // Special handling for cancel counter button in legacy mode
  if (buttonType === "counter_cancel") {
    const s = norm(status);
    return s === "COUNTER_SUBMITTED_ACKED" || 
           s === "COUNTER_CANCELED_FAILED"
  }

  // Special handling for COUNTER_CANCELED_ACKED in legacy mode
  if (norm(status) === "COUNTER_CANCELED_ACKED") {
    return buttonType !== "counter_cancel";
  }

  switch (status.toLowerCase()) {
    case ORDER_STATUS.RECEIVED:
      return true;
    case ORDER_STATUS.COUNTER_REJECTED:
      return buttonType !== "counter";
    case ORDER_STATUS.COUNTER_ACCEPTED:
    case ORDER_STATUS.COUNTER_SUBMITTED:
      return buttonType === "accept" || buttonType === "reject";
    case ORDER_STATUS.PENDING:
    case ORDER_STATUS.COUNTER_SUBMITED: // Keep for backward compatibility
    case ORDER_STATUS.EXPIRED:
    case ORDER_STATUS.REJECTED:
    case ORDER_STATUS.ACCEPTED:
      return false;
    default:
      return false;
  }
};

/**
 * Check if any quote in the venues array has been accepted
 */
export const hasAcceptedQuote = (venues) => {
  if (!venues || !Array.isArray(venues)) return false;
  return venues.some(venue => venue.status?.toLowerCase() === ORDER_STATUS.ACCEPTED);
};

//check if all quotes are rejected
export const hasAllQuotesRejected = (venues) => {
  if (!venues || !Array.isArray(venues)) return false;
  return venues.every(venue => venue.status?.toLowerCase() === ORDER_STATUS.REJECTED);
};

/**
 * Check if a quote is expired based on its quoteExpiryDtTime using UTC time
 */
export const isQuoteExpired = (quote) => {
  if (!quote || !quote.quoteExpiryDtTime) return false;

  try {
    // Ensure the timestamp is treated as UTC by appending 'Z' if it doesn't already have one
    let expiryTimeStr = quote.quoteExpiryDtTime;
    if (!expiryTimeStr.endsWith('Z')) {
      expiryTimeStr += 'Z';
    }

    const expiryTime = new Date(expiryTimeStr).getTime(); // UTC timestamp in milliseconds
    const currentTime = Date.now(); // Current UTC timestamp in milliseconds
    return currentTime > expiryTime;
  } catch (e) {
    console.error("Error checking if quote is expired:", e);
    return false;
  }
};

/**
 * Check if an order is expired based on its orderExpiryDtTime using UTC time
 */
export const isOrderExpired = (order) => {
  if (!order || !order.orderExpiryDtTime) return false;

  try {
    // Ensure the timestamp is treated as UTC by appending 'Z' if it doesn't already have one
    let expiryTimeStr = order.orderExpiryDtTime;
    if (!expiryTimeStr.endsWith('Z')) {
      expiryTimeStr += 'Z';
    }

    const expiryTime = new Date(expiryTimeStr).getTime(); // UTC timestamp in milliseconds
    const currentTime = Date.now(); // Current UTC timestamp in milliseconds

    return currentTime > expiryTime;
  } catch (e) {
    console.error("Error checking if order is expired:", e);
    return false;
  }
};
